import { Leaf } from "lucide-react";
import FadeIn from "./FadeIn";
import { C } from "./brandTokens";

/**
 * What an actual morning inside Eden Pages looks like — presented as a
 * believable application screenshot rather than an editorial content card.
 *
 * Composition (from outside in):
 *  - Browser-window chrome (traffic lights + URL pill) signals "this is
 *    the product running in a real browser."
 *  - Minimal app header (wordmark + leaf, current date, day counter)
 *    signals "this is what the app's main surface looks like."
 *  - The morning entry, scripture, pastoral reflection, and closing
 *    prayer fill the viewport exactly as they appear in the running app.
 *
 * Pure HTML/CSS — no images, no AI artifacts. Sharp at every viewport,
 * responsive automatically, and trivially tweakable from one source.
 */
export default function ProductVisualSection() {
  return (
    <section
      id="the-product"
      className="py-[100px] px-6 sm:px-10"
      style={{ background: C.warm }}
    >
      <div className="max-w-[1080px] mx-auto">

        {/* Section heading */}
        <FadeIn className="text-center">
          <p
            className="font-ui"
            style={{
              fontSize: 11,
              color: C.txt3,
              letterSpacing: "1.4px",
              marginBottom: 24,
            }}
          >
            INSIDE THE APP
          </p>
          <h2
            className="font-heading font-semibold text-spec-brown mx-auto text-center"
            style={{
              fontSize: "clamp(32px, 4vw, 48px)",
              lineHeight: 1.1,
              maxWidth: 580,
            }}
          >
            A morning, as it appears
            <br />
            <em className="italic text-spec-sage">inside Eden Pages.</em>
          </h2>
          <p
            className="font-ui mx-auto text-center"
            style={{
              fontSize: 15,
              color: C.txt2,
              lineHeight: 1.75,
              maxWidth: 520,
              marginTop: 20,
            }}
          >
            One unhurried surface. Three blocks. Everything tuned for presence,
            not productivity.
          </p>
        </FadeIn>

        {/* Browser window frame */}
        <FadeIn delay={0.15} style={{ marginTop: 64 }}>
          <div
            className="mx-auto"
            style={{
              maxWidth: 960,
              background: C.white,
              border: `1px solid rgba(61,50,37,0.08)`,
              borderRadius: 8,
              overflow: "hidden",
              boxShadow:
                "0 32px 80px rgba(61,50,37,0.14), 0 8px 24px rgba(61,50,37,0.06)",
            }}
          >
            {/* macOS-style browser chrome */}
            <div
              className="flex items-center"
              style={{
                background: "#EFECE5",
                borderBottom: "1px solid rgba(61,50,37,0.08)",
                padding: "12px 16px",
                gap: 14,
              }}
            >
              {/* Traffic lights */}
              <div className="flex items-center" style={{ gap: 7 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 9999,
                    background: "#FF5F57",
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 9999,
                    background: "#FEBC2E",
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 9999,
                    background: "#28C840",
                    display: "inline-block",
                  }}
                />
              </div>

              {/* URL pill */}
              <div
                className="flex-1 mx-auto flex items-center justify-center"
                style={{
                  background: "rgba(255,255,255,0.65)",
                  border: "1px solid rgba(61,50,37,0.06)",
                  borderRadius: 6,
                  padding: "5px 14px",
                  maxWidth: 360,
                }}
              >
                <span
                  className="font-ui"
                  style={{
                    fontSize: 12,
                    color: C.txt3,
                    letterSpacing: "0.1px",
                  }}
                >
                  edenpages.com/journal
                </span>
              </div>

              {/* Spacer to balance traffic lights — keeps URL centered */}
              <div style={{ width: 54 }} aria-hidden="true" />
            </div>

            {/* App viewport */}
            <div style={{ background: C.cream }}>
              {/* App top header */}
              <div
                className="flex items-center justify-between"
                style={{
                  padding: "20px 28px",
                  borderBottom: `1px solid ${C.bdr}`,
                }}
              >
                {/* Wordmark */}
                <div className="flex items-center" style={{ gap: 9 }}>
                  <div
                    className="flex items-center justify-center rounded-full"
                    style={{
                      width: 26,
                      height: 26,
                      background: C.sageGlow,
                      border: `1px solid ${C.sageBdr}`,
                    }}
                  >
                    <Leaf size={12} className="text-spec-sage" />
                  </div>
                  <span
                    className="font-heading font-semibold text-spec-brown"
                    style={{ fontSize: 15, letterSpacing: "0.2px" }}
                  >
                    Eden Pages
                  </span>
                </div>

                {/* Right side — date + streak */}
                <div className="flex items-center" style={{ gap: 18 }}>
                  <span
                    className="font-heading italic hidden sm:inline"
                    style={{ fontSize: 13, color: C.txt3 }}
                  >
                    Tuesday · November 12
                  </span>
                  <span
                    className="flex items-center"
                    style={{
                      gap: 6,
                      fontSize: 11,
                      color: C.sage,
                      letterSpacing: "0.5px",
                      fontFamily: "var(--font-ui, 'Jost', sans-serif)",
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 9999,
                        background: C.sage,
                        display: "inline-block",
                      }}
                    />
                    DAY 23
                  </span>
                </div>
              </div>

              {/* Main entry surface */}
              <div
                className="mx-auto"
                style={{
                  maxWidth: 620,
                  padding: "clamp(36px, 5vw, 56px) clamp(24px, 5vw, 48px) clamp(40px, 6vw, 64px)",
                }}
              >
                {/* Block A — User journal entry */}
                <p
                  className="font-heading"
                  style={{
                    fontSize: "clamp(17px, 1.9vw, 20px)",
                    lineHeight: 1.85,
                    color: C.brown,
                    marginBottom: 36,
                  }}
                >
                  I keep coming back to Martha — how Jesus doesn't scold her
                  for the doing, only for losing herself in it. I'm doing so
                  much right now and I can't tell if I'm serving or just
                  hiding behind a to-do list.
                </p>

                {/* Block B — Persona divider */}
                <div
                  className="flex items-center"
                  style={{
                    gap: 10,
                    marginBottom: 24,
                    color: C.sage,
                  }}
                >
                  <div
                    className="flex-1"
                    style={{ height: 1, background: C.sageBdr }}
                  />
                  <Leaf size={11} style={{ color: C.sage }} />
                  <span
                    className="font-ui"
                    style={{
                      fontSize: 10,
                      color: C.sage,
                      letterSpacing: "1.8px",
                    }}
                  >
                    PASTOR
                  </span>
                  <div
                    className="flex-1"
                    style={{ height: 1, background: C.sageBdr }}
                  />
                </div>

                {/* Block C — Scripture */}
                <div
                  style={{
                    borderLeft: `2px solid ${C.sageBdr}`,
                    paddingLeft: 22,
                    marginBottom: 24,
                  }}
                >
                  <p
                    className="font-ui"
                    style={{
                      fontSize: 10,
                      color: C.sage,
                      letterSpacing: "1.8px",
                      marginBottom: 8,
                    }}
                  >
                    LUKE 10:41–42
                  </p>
                  <p
                    className="font-heading italic m-0"
                    style={{
                      fontSize: "clamp(16px, 1.8vw, 19px)",
                      lineHeight: 1.78,
                      color: C.txt,
                    }}
                  >
                    "Martha, Martha, you are worried and upset about many
                    things, but few things are needed — or indeed only one.
                    Mary has chosen what is better, and it will not be taken
                    away from her."
                  </p>
                </div>

                {/* Block D — Pastoral reflection */}
                <p
                  className="font-ui"
                  style={{
                    fontSize: 14.5,
                    lineHeight: 1.78,
                    color: C.txt2,
                    marginBottom: 32,
                  }}
                >
                  You named the fear already — that the serving might be
                  hiding. Jesus doesn't tell Martha to stop serving. He tells
                  her she's worried. The question isn't what you do today.
                  It's whether you can do it from a centered place.
                </p>

                {/* Separator */}
                <div
                  style={{
                    height: 1,
                    background: C.bdr,
                    marginBottom: 28,
                  }}
                />

                {/* Block E — Closing prayer */}
                <div>
                  <p
                    className="font-ui"
                    style={{
                      fontSize: 10,
                      color: C.gold,
                      letterSpacing: "1.8px",
                      marginBottom: 8,
                    }}
                  >
                    CLOSING PRAYER
                  </p>
                  <p
                    className="font-heading italic m-0"
                    style={{
                      fontSize: "clamp(15px, 1.7vw, 18px)",
                      lineHeight: 1.85,
                      color: C.txt2,
                    }}
                  >
                    "Lord, help me serve without losing myself in the serving.
                    Let the doing flow from the being. When I am anxious
                    today, remind me that few things are needed — and that
                    you are the one. Amen."
                  </p>
                </div>
              </div>

              {/* App footer toolbar — subtle save indicator + word count */}
              <div
                className="flex items-center justify-between"
                style={{
                  padding: "12px 28px",
                  borderTop: `1px solid ${C.bdr}`,
                  background: "rgba(255,255,255,0.4)",
                }}
              >
                <span
                  className="font-ui flex items-center"
                  style={{
                    fontSize: 11,
                    color: C.txt3,
                    gap: 7,
                    letterSpacing: "0.2px",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 9999,
                      background: C.sage,
                      display: "inline-block",
                    }}
                  />
                  Saved
                </span>
                <span
                  className="font-ui"
                  style={{
                    fontSize: 11,
                    color: C.txt3,
                    letterSpacing: "0.2px",
                  }}
                >
                  142 words
                </span>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Caption below the screenshot */}
        <FadeIn delay={0.3} style={{ marginTop: 36 }}>
          <p
            className="font-heading italic text-center"
            style={{
              fontSize: 13,
              color: C.txt3,
            }}
          >
            The actual surface, with one real morning's words. Yours fills the
            blank.
          </p>
        </FadeIn>

      </div>
    </section>
  );
}
